'use client';

import { Button } from '@repo/design-system/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/design-system/components/ui/dialog';
import { Input } from '@repo/design-system/components/ui/input';
import { useToast } from '@repo/design-system/hooks/use-toast';
import { cn } from '@repo/design-system/lib/utils';
import { Info, Mail, X } from 'lucide-react';

import { useSearchParams, usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { setEnvironment } from '@/lib/api-config';

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [email, setEmail] = useState('');
  const [sentToEmail, setSentToEmail] = useState('');
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { toast } = useToast();
  const [toastShown, setToastShown] = useState(false);

  // Detect environment from URL path
  const currentEnvironment = pathname?.includes('/sandbox') ? 'sandbox' : 'production';

  // Get redirect URL from query params (supports both 'redirect' and 'returnTo')
  const redirectParam = searchParams.get('redirect') || searchParams.get('returnTo');

  // Build full absolute URL for redirect (backend requires absolute URLs).
  // Rejects external URLs to prevent open redirect phishing.
  const buildAbsoluteRedirectUrl = (path: string | null): string | null => {
    if (!path) return null;

    // Reject absolute URLs to external origins (open redirect prevention)
    if (path.startsWith('http://') || path.startsWith('https://')) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      if (!origin) return null;
      try {
        const url = new URL(path);
        if (url.origin !== origin) return null;
        // Enforce sandbox isolation: sandbox magic links must redirect to /sandbox paths
        if (currentEnvironment === 'sandbox' && !url.pathname.startsWith('/sandbox')) {
          const normalizedPath = url.pathname.startsWith('/') ? url.pathname : `/${url.pathname}`;
          return `${origin}/sandbox${normalizedPath}${url.search}${url.hash}`;
        }
        // Enforce production isolation: production magic links must not redirect to /sandbox
        if (currentEnvironment === 'production' && url.pathname.startsWith('/sandbox')) {
          return null;
        }
        return path;
      } catch {
        return null;
      }
    }

    // Reject scheme-relative URLs (e.g., //evil.com) which could bypass origin checks
    if (path.startsWith('//')) return null;

    // Build absolute URL from current origin for relative paths
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (!origin) return null;

    // Ensure path starts with /
    let normalizedPath = path.startsWith('/') ? path : `/${path}`;

    // For sandbox environment, ensure path includes /sandbox prefix
    if (currentEnvironment === 'sandbox' && !normalizedPath.startsWith('/sandbox')) {
      normalizedPath = `/sandbox${normalizedPath}`;
    }
    // For production, reject sandbox paths (environment isolation)
    if (currentEnvironment === 'production' && normalizedPath.startsWith('/sandbox')) {
      return null;
    }

    return `${origin}${normalizedPath}`;
  };

  // Auto-close modal after 5 seconds, then show banner
  useEffect(() => {
    if (showModal) {
      const timer = setTimeout(() => {
        setShowModal(false);
        setShowBanner(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showModal]);

  // Auto-dismiss banner after 30 seconds
  useEffect(() => {
    if (showBanner) {
      const timer = setTimeout(() => {
        setShowBanner(false);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [showBanner]);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
    // Show toast for session_revoked or other messages
    const message = searchParams.get('message');
    if (!toastShown && message) {
      if (message === 'session_revoked') {
        toast({
          title: 'Session Revoked',
          description: 'Your session was revoked. Please sign in again.',
        });
        setToastShown(true);
      } else if (message === 'account_disabled') {
        toast({
          title: 'Account Disabled',
          description:
            'Your account has been disabled. Please contact support.',
        });
        setToastShown(true);
      } else if (message === 'portal_access_required') {
        toast({
          title: 'Sign In Again',
          description:
            'Your contractor portal access changed since you last signed in. Request a new login link below.',
        });
        setToastShown(true);
      }
    }

    // Surface magic-link failures. The callback route redirects here with
    // ?error=... on every failure path, but nothing read it — so a contractor
    // whose link had expired, been consumed by an inbox scanner, or failed
    // verification landed on a bare sign-in form with no idea what happened,
    // and told support "the page goes nowhere".
    const errorParam = searchParams.get('error');
    if (!toastShown && errorParam) {
      const description =
        errorParam === 'invalid-token'
          ? 'That login link is no longer valid — links can only be used once and expire after 15 minutes. Request a new one below.'
          : errorParam === 'missing-token'
            ? 'That link was missing its login code. Request a new one below.'
            : errorParam === 'csrf'
              ? 'We could not verify where that link was opened from. Request a new one below and open it directly from your email.'
              : 'We could not complete sign in with that link. Request a new one below.';

      toast({
        title: 'Sign In Link Problem',
        description,
      });
      setToastShown(true);
    }
  }, [searchParams, toast, toastShown]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const emailValue = formData.get('email') as string;

    if (!emailValue) {
      setError('Please enter your email address.');
      setLoading(false);
      return;
    }

    // Set environment in localStorage before sending link
    setEnvironment(currentEnvironment);

    try {
      const redirectUrl = buildAbsoluteRedirectUrl(redirectParam);
      // Send magic link directly - backend will check if user exists in database
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/v1/auth/send-magic-link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Ten99Policy-Environment': currentEnvironment,
          },
          body: JSON.stringify({
            email: emailValue,
            portal: 'contractor_portal',
            environment: currentEnvironment,
            ...(redirectUrl && { redirect_url: redirectUrl }),
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setShowModal(true);
        setSentToEmail(emailValue);
        setError(null);
      } else {
        // Backend will return appropriate error if user doesn't exist
        setError(
          data.message ||
            'Failed to send login link. Please check your email and try again.'
        );
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Magic link error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      {/* Logo and Welcome */}
      <div className="mb-8 text-center">
        <div className="mx-auto mb-6 flex items-center justify-center">
          <svg
            width="40"
            height="40"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.68928 5.31641H21.6782L27.617 15.603H22.7236L18.9888 9.13437H8.89358L6.68928 5.31641ZM9.94074 19.421L4 9.13437H8.89358L12.6283 15.603H22.7236L24.9278 19.421H14.8899L17.9287 24.6844H12.9796L9.94074 19.421Z"
              fill="#3333FF"
            />
          </svg>
        </div>
        <h1 className="mb-2 font-semibold text-2xl text-foreground">
          Welcome back
        </h1>
        <p className="text-muted-foreground text-sm">
          Don&#39;t have access? Contact your administrator for an invitation.
        </p>
      </div>

      {/* Magic Link Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Input
            id="email"
            name="email"
            type="email"
            aria-label="Email address"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              // Clear banner if user changes email to a different address
              if (showBanner && e.target.value !== sentToEmail) {
                setShowBanner(false);
              }
            }}
            required
            disabled={loading}
            className="h-12"
          />
        </div>

        {/* Info Banner */}
        {showBanner && sentToEmail && (
          <div className="flex items-start gap-2 rounded-md border border-border bg-muted p-3 text-muted-foreground text-sm">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
            <div className="flex-1">
              <p className="font-medium">Check your email</p>
              <p className="mt-1 text-muted-foreground">
                If <strong>{sentToEmail}</strong> is registered, we've sent a
                login link. The link expires in 15 minutes.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowBanner(false)}
              className="flex-shrink-0 text-muted-foreground hover:text-foreground"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-destructive text-sm">
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="h-12 w-full bg-primary font-medium text-primary-foreground hover:bg-primary/90"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg
                className="-ml-1 mr-3 h-5 w-5 animate-spin text-primary-foreground"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Sending login link...
            </>
          ) : (
            'Send me a login link'
          )}
        </Button>

        {/* Legal */}
        <p className="text-center text-muted-foreground text-xs">
          You acknowledge that you read, and agree, to our{' '}
          <a
            href="https://www.1099policy.com/terms/user"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="https://www.1099policy.com/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            Privacy Policy
          </a>
        </p>
      </form>

      {/* Success Modal */}
      <Dialog
        open={showModal}
        onOpenChange={(open) => {
          setShowModal(open);
          if (!open) {
            // If user closes modal manually, show banner
            setShowBanner(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-center">Check Your Email</DialogTitle>
            <div className="space-y-2 pt-2 text-center text-muted-foreground text-sm">
              <p>
                If <strong className="text-foreground">{sentToEmail}</strong> is
                registered in our system, we've sent a secure login link to your
                inbox.
              </p>
              <p className="text-sm">
                If you're not registered yet, please contact your administrator
                for an invitation.
              </p>
              <p className="pt-2 text-muted-foreground text-xs">
                The link expires in 15 minutes.
              </p>
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
